import React, { useState } from 'react';
import agents from '../data/agents.json';
import './Dashboard.css';

const Dashboard = ({ user, onLogout, onStartDemo }) => {
  const [hoveredAgent, setHoveredAgent] = useState(null);

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-left">
          <div className="dashboard-logo">
            <div className="dashboard-logo-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div className="dashboard-logo-text">
              <span className="dashboard-logo-title">Agentic</span>
              <span className="dashboard-logo-subtitle">Corporate Finance</span>
            </div>
          </div>
        </div>
        
        <div className="dashboard-header-right">
          <div className="dashboard-user">
            <div className="dashboard-user-info">
              <span className="dashboard-user-name">{user.name}</span>
              <span className="dashboard-user-role">{user.role}</span>
            </div>
            <div className="dashboard-user-avatar">{user.avatar}</div>
          </div>
          <button className="dashboard-logout" onClick={onLogout}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Agents Grid */}
        <div className="dashboard-agents-grid">
          {agents.agents.map((agent, index) => (
            <div
              key={agent.id}
              className={`dashboard-agent-card ${hoveredAgent === agent.id ? 'hovered' : ''} ${index < 6 ? 'ready' : 'coming-soon'}`}
              onMouseEnter={() => setHoveredAgent(agent.id)}
              onMouseLeave={() => setHoveredAgent(null)}
              onClick={() => index < 6 && onStartDemo(agent.id)}
            >
              {/* Agent Number */}
              <div className="dashboard-agent-number">{String(index + 1).padStart(2, '0')}</div>
              
              {/* Status Indicator */}
              <div className={`dashboard-agent-status-dot ${index < 6 ? 'active' : 'inactive'}`} />

              {/* Icon */}
              <div className="dashboard-agent-icon">{agent.icon}</div>

              {/* Content */}
              <h3 className="dashboard-agent-name">{agent.name}</h3>
              <p className="dashboard-agent-tagline">{agent.tagline}</p>

              {/* Sub-agents */}
              <div className="dashboard-agent-meta">
                <div className="dashboard-agent-subagents">
                  {agent.subAgents.slice(0, 5).map((sub, i) => (
                    <span key={i} className="dashboard-subagent-dot" title={sub.name} />
                  ))}
                </div>
                <span className="dashboard-agent-subcount">{agent.subAgents.length} agents</span>
              </div>

              {/* Hover Action Hint */}
              <div className="dashboard-agent-action">
                {index < 6 ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    <span>Click to start</span>
                  </>
                ) : (
                  <span className="coming-soon-text">Coming Soon</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="dashboard-stats-bar">
          <div className="dashboard-stat-item">
            <div className="dashboard-stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
            </div>
            <div className="dashboard-stat-content">
              <span className="dashboard-stat-value">6</span>
              <span className="dashboard-stat-label">Active Agents</span>
            </div>
          </div>
          <div className="dashboard-stat-item">
            <div className="dashboard-stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="dashboard-stat-content">
              <span className="dashboard-stat-value">~3.5 min</span>
              <span className="dashboard-stat-label">Demo Duration</span>
            </div>
          </div>
          <div className="dashboard-stat-item">
            <div className="dashboard-stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              </svg>
            </div>
            <div className="dashboard-stat-content">
              <span className="dashboard-stat-value">30</span>
              <span className="dashboard-stat-label">Sub-Agents</span>
            </div>
          </div>
          <div className="dashboard-stat-item">
            <div className="dashboard-stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className="dashboard-stat-content">
              <span className="dashboard-stat-value">&lt;$0.50</span>
              <span className="dashboard-stat-label">Avg. Cost</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="dashboard-footer-left">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>Visual demonstration with simulated data</span>
        </div>
        <div className="dashboard-footer-right">
          <span>Built by ACC</span>
          <span className="dashboard-footer-separator">•</span>
          <span>AWS GenAI Competency Partner</span>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
